import React, { createContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';

import { useAuth } from 'contexts/auth';
import { emailRegex } from 'utils/constants';
import { useSnackbar } from 'contexts/snackbar';

const defaultForm = {
    email: '',
    code: '',
    password: '',
    confirmPassword: '',
};

const PAGES = {
    ENTER_EMAIL: 'enter-email',
    EMAIL_SENT: 'email-sent',
    NEW_PASSWORD: 'new-password',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const history = useHistory();
    const { message } = useSnackbar();
    const { forgotPassword, confirmForgotPassword, verifyUser } = useAuth();

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        'enter-email': {
            email: '',
        },
        'new-password': {
            password: '',
            confirmPassword: '',
        },
    });
    const [currentStep, setCurrentStep] = useState(PAGES.ENTER_EMAIL);
    const [satisfyRequirement, setSatisfyRequirement] = useState({
        validateMoreThan8Characters: true,
        validateOneUpperCase: true,
        validateOneLowerCase: true,
        validateOneNumber: true,
        validateOneSpecial: true,
    });

    const getErrorMessage = (key, value, form) => {
        value = value?.trim();
        switch (key) {
            case 'email':
                return value && emailRegex.test(value) ? '' : 'Please enter a valid email';
            case 'password':
                if (Object.values(satisfyRequirement).find((requirement) => requirement))
                    return 'Please satisfy all requirements';
                return value ? '' : 'Please enter password';
            case 'confirmPassword':
                return form.password === value ? '' : 'Passwords do not match';
            default:
                return '';
        }
    };

    const handleSendForgotPasswordCode = () => {
        if (!validateAll()) return;
        setLoading(true);
        forgotPassword(form.email?.toLowerCase()?.trim());
        setLoading(false);
        handleNext(PAGES.EMAIL_SENT);
        return message.success('If this email is registered with an account, you will be receiving an email soon.');
    };

    const handleConfirmationCode = async (code) => {
        const verifyTheUser = await verifyUser(form.email?.toLowerCase(), code);
        if (verifyTheUser === 'SUCCESS') return history.push('/');
    };

    const handleUpdatePassword = async () => {
        if (!validateAll()) return;
        setLoading(true);
        const updatePassword = await confirmForgotPassword(form.email?.toLowerCase()?.trim(), form.code, form.password);
        setLoading(false);
        if (!updatePassword) message.error('The code is expired. Please reset your password again.');
        else message.success('Your password was reset.');
        return history.push('/');
    };

    const handleResendCode = async () => {
        message.success('If this email is registered with an account, you will be receiving an email soon.');
        return await forgotPassword(form.email?.toLowerCase());
    };

    const handleChange = async (key, data) => {
        switch (key) {
            case 'email': {
                return setForm((prev) => ({ ...prev, email: data }));
            }
            case 'password': {
                return setForm((prev) => ({ ...prev, password: data }));
            }
            case 'confirm-password': {
                return setForm((prev) => ({ ...prev, confirmPassword: data }));
            }
            case 'code': {
                return setForm((prev) => ({ ...prev, code: data }));
            }
            default:
                return;
        }
    };

    const handleNext = (step) => {
        setCurrentStep(step);
    };

    const handleNewPasswordRequirement = (password) => {
        const atLeastOneUpperCase = /^.*[A-Z]+.*$/;
        const atLeastOneLowerCase = /^.*[a-z]+.*$/;
        const atLeastOneNumber = /^.*[0-9]+.*$/;
        const atLeast8Character = /^[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{8,20}$/;
        const atLeastOneSpecial = /^(?=.*[!@#$%^&*]).*$/;
        const validateOneUpperCase = atLeastOneUpperCase.exec(password);
        const validateOneLowerCase = atLeastOneLowerCase.exec(password);
        const validateOneNumber = atLeastOneNumber.exec(password);
        const validateMoreThan8Characters = atLeast8Character.exec(password);
        const validateOneSpecial = atLeastOneSpecial.exec(password);
        setSatisfyRequirement({
            validateOneUpperCase: !validateOneUpperCase ? true : false,
            validateOneLowerCase: !validateOneLowerCase ? true : false,
            validateOneNumber: !validateOneNumber ? true : false,
            validateMoreThan8Characters: !validateMoreThan8Characters ? true : false,
            validateOneSpecial: !validateOneSpecial ? true : false,
        });
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value, form);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors[currentStep])
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key], form);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    useEffect(() => {
        if (form.email?.length > 0) {
            setForm((prev) => {
                return { ...prev, email: form.email };
            });
        }
    }, [form?.email]);

    useEffect(() => {
        handleNewPasswordRequirement(form.password);
    }, [form?.password, form?.confirmPassword]);

    return (
        <PageContext.Provider
            value={{
                loading,
                form,
                errors,
                currentStep,
                satisfyRequirement,

                handleChange,
                handleNext,
                handleConfirmationCode,
                handleSendForgotPasswordCode,
                handleUpdatePassword,
                handleResendCode,
                setErrors,
                setForm,
                setLoading,
                PAGES,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
